import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { TopbarComponent } from '../shared/components/topbar/topbar.component';
import { SidebarComponent } from '../shared/components/sidebar/sidebar.component';
import { BottombarComponent } from '../shared/components/bottombar/bottombar.component';
import { AdminService } from '../shared/services/admin.service';

@Component({
    selector: 'app-reconciliation',
    templateUrl: './reconciliation.component.html',
    styleUrls: ['./reconciliation.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        RouterModule,
        FormsModule,
        TopbarComponent,
        SidebarComponent,
        BottombarComponent,
    ]
})
export class ReconciliationComponent implements OnInit {
  reconciliation: any[] | null = null;
  details;
  canAdjust: boolean = false;
  showAdjustModal: boolean = false;
  showAdjustDetails: boolean = false;
  showGuide: boolean = false;
  adjustDetailsTarget: any = null;
  isSaving: boolean = false;
  isLoading: boolean = false;
  loadError: string = '';
  adjustError: string = '';
  adjustTarget: any = null;
  adjustForm: any = { corrected_balance: null, reason: '' };
  adjustIdempotencyKey: string = '';
  lastUpdated: Date | null = null;

  private readonly moneyFormat = new Intl.NumberFormat('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  constructor(
    private adminService: AdminService,
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService,
    private router: Router
  ) {}

  ngOnInit() {
    this.getDetails();
  }

  load() {
    this.isLoading = true;
    this.loadError = '';
    this.adminService.getReconciliation().subscribe({
      next: (data) => {
        this.reconciliation = Array.isArray(data) ? data : [data];
        this.isLoading = false;
        this.lastUpdated = new Date();
        this.refresh();
      },
      error: () => {
        this.isLoading = false;
        this.loadError = 'Unable to load reconciliation data. Please try again.';
        this.refresh();
      },
    });
  }

  getDetails() {
    this.adminService.getDetails().subscribe({
      next: (data) => {
        this.details = data[0];
        this.canAdjust = !!(this.details && this.details.permission === 'super_admin');
        this.reconciliation = [];
        if (this.canAdjust) {
          this.load();
        } else {
          this.router.navigate(['/dashboard']);
        }
        this.refresh();
      },
      error: () => {
        this.reconciliation = [];
        this.refresh();
      },
    });
  }

  get rows(): any[] {
    return this.reconciliation || [];
  }

  get platformRow(): any {
    return this.rows.find((r) => r?.agent?.slug === 'platform') || null;
  }

  get partnerRows(): any[] {
    return this.rows.filter((r) => r?.agent?.slug !== 'platform');
  }

  get closeCount(): number {
    return this.partnerRows.filter((r) => r?.is_close === true).length;
  }

  get reviewCount(): number {
    return this.partnerRows.filter((r) => r?.is_close === false).length;
  }

  get totalUnexplained(): number {
    return this.round(
      this.partnerRows.reduce((acc, r) => acc + (this.isNumber(r?.unexplained) ? Number(r.unexplained) : 0), 0)
    );
  }

  get totalAdjustments(): number {
    return this.round(
      this.partnerRows.reduce(
        (acc, r) => acc + (this.isNumber(r?.adjustments?.sum) ? Number(r.adjustments.sum) : 0),
        0
      )
    );
  }

  get adjustPreviewChange(): number | null {
    if (!this.adjustTarget || !this.isNumber(this.adjustForm?.corrected_balance)) {
      return null;
    }
    const current = this.isNumber(this.adjustTarget.wallet_balance)
      ? Number(this.adjustTarget.wallet_balance)
      : 0;
    return this.round(Number(this.adjustForm.corrected_balance) - current);
  }

  get adjustValid(): boolean {
    return (
      !!this.adjustTarget &&
      this.isNumber(this.adjustForm?.corrected_balance) &&
      !!String(this.adjustForm?.reason || '').trim()
    );
  }

  historyFor(row: any): any[] {
    return row && Array.isArray(row.adjustment_details) ? row.adjustment_details : [];
  }

  historyNet(row: any): number {
    return this.round(
      this.historyFor(row).reduce(
        (acc, a) => acc + (this.isNumber(a?.change_amount) ? Number(a.change_amount) : 0),
        0
      )
    );
  }

  money(value: any): string {
    if (!this.isNumber(value)) {
      return '—';
    }
    return 'NGN ' + this.moneyFormat.format(Number(value));
  }

  signedMoney(value: any): string {
    if (!this.isNumber(value)) {
      return '—';
    }
    const v = Number(value);
    const sign = v > 0 ? '+' : v < 0 ? '\u2212' : '';
    return sign + 'NGN ' + this.moneyFormat.format(Math.abs(v));
  }

  changeClass(value: any): string {
    if (!this.isNumber(value) || Number(value) === 0) {
      return 'recon-mute';
    }
    return Number(value) > 0 ? 'recon-pos' : 'recon-neg';
  }

  openAdjust(row) {
    if (!row || !row.agent || !row.agent.id) {
      return;
    }
    this.adjustTarget = row;
    this.adjustForm = {
      corrected_balance: this.isNumber(row.wallet_balance) ? row.wallet_balance : null,
      reason: '',
    };
    this.adjustIdempotencyKey = this.newIdempotencyKey();
    this.adjustError = '';
    this.showAdjustModal = true;
  }

  closeAdjust() {
    this.showAdjustModal = false;
    this.adjustTarget = null;
    this.adjustIdempotencyKey = '';
    this.adjustError = '';
  }

  openAdjustDetails(row) {
    if (!row || !row.agent || !row.agent.id) {
      return;
    }
    this.adjustDetailsTarget = row;
    this.showAdjustDetails = true;
  }

  closeAdjustDetails() {
    this.showAdjustDetails = false;
    this.adjustDetailsTarget = null;
  }

  submitAdjust() {
    if (!this.adjustValid) {
      this.adjustError = 'Corrected balance and reason are required.';
      return;
    }

    this.isSaving = true;
    this.adjustError = '';

    const data = {
      agent_id: this.adjustTarget.agent.id,
      corrected_balance: this.adjustForm.corrected_balance,
      reason: String(this.adjustForm.reason).trim(),
      idempotency_key: this.adjustIdempotencyKey,
    };

    this.adminService.adjustAgentBalance(data).subscribe(
      (response) => {
        this.isSaving = false;
        if (response && response.status) {
          const body = response.data || {};
          if (body.duplicate) {
            this.toastr.info('This adjustment was already applied.');
          } else if (Number(body.change_amount) === 0) {
            this.toastr.warning('No balance change was applied for this request.');
          } else {
            this.toastr.success(
              'Wallet adjusted by ' + this.signedMoney(body.change_amount)
            );
          }
          this.closeAdjust();
          this.load();
        } else {
          this.adjustError =
            (response && response.message) || 'Adjustment failed.';
        }
      },
      (error) => {
        this.isSaving = false;
        this.adjustError =
          (error && error.error && error.error.message) ||
          'Adjustment failed.';
      }
    );
  }

  private newIdempotencyKey(): string {
    const c = (globalThis as any).crypto;
    if (c && typeof c.randomUUID === 'function') {
      return c.randomUUID();
    }
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }

  private isNumber(value: any): boolean {
    return value !== null && value !== undefined && value !== '' && !Number.isNaN(Number(value));
  }

  private round(value: number): number {
    return Math.round(value * 100) / 100;
  }

  refresh() {
    try {
      this.cdr.detectChanges();
    } catch (e) { void e; }
  }
}
